import {getGlobal, isNodeJS} from "../../compat";
import {createWorker} from "../DtWorkerCreator";
import {copySanitizedLoadContext} from "../../net/endpoints";
import {blobToJson} from "../encoding/utf8";

import {PriorityQueue} from "./PriorityQueue";

import {av, avp} from "@adsk/lmv";

export var MESH_RECEIVE_EVENT = "meshReceived";
export var MESH_FAILED_EVENT = "meshFailed";
export var MATERIAL_RECEIVE_EVENT = "materialReceived";
export var MATERIAL_FAILED_EVENT = "materialFailed";

const MAX_REQUESTS_PER_WORKER = 10000;
const INITIAL_REQUESTS_PER_WORKER = 200;

let useOpfs;

function initLoadContextGeomCache(msg, loadContext) {

	if (useOpfs === undefined) {
		// ENABLE_MODEL_CACHE is set via the viewer settings panel.
		useOpfs = !isNodeJS() && !!getGlobal().ENABLE_MODEL_CACHE;
	}
	copySanitizedLoadContext(msg, loadContext);
	msg.useOpfs = useOpfs;
	return msg;
}

// Helper function used for cache cleanup
function compareGeomsByImportance(geom1, geom2) {
	return geom1.importance - geom2.importance;
}

/** Shared cache of BufferGeometries and material JSONs used by different OtgLoaders. */
export function DtResourceCache(sessionId) {

	// all geometries, indexed by geom hashes
	var _geoms = this._geoms = new Map();
	var _mats = new Map();

	// A single geometry may be requested by one or more model loaders.
	// This map keeps track of requests already in progress so that
	// we don't issue multiple simultaneously
	var _hash2Requests = new Map();

	var _workTotal = 0;
	var _workDone = 0;
	var _currentProgress = -1;
	// Store the first timestamp at creation time to measure load times from viewer initialization across all models.
	var _timings = [performance.now()];

	// worker for geometry loading
	var _worker = createWorker('Geometry Worker', sessionId);
	_worker.addEventListener('message', handleMessage);

	// This value changes with the number of enqueued requests.
	// We start with a low value to properly prioritize more queued up requests first.
	var _requestsPerWorker = INITIAL_REQUESTS_PER_WORKER;
	var _numLoadersDownloadingFragmentList = 0;

	this.initialized = true;

	// track memory consumption
	this.byteSize = 0;
	this.refCount = 0;

	// A request is called in-progress if we have sent it to the worker and didn't receive a result yet.
	// We restrict the number of _requestsInProgress. If the limit is reached, all additional requests
	// are enqueued in _waitingRequests.
	var _requestsInProgress = 0;
	var _totalRequested = 0;
	var _totalReceived = 0;
	var _lifeTimeTotal = 0;

	var _timeout = undefined;
	var _recurringProcessQueuedItemsCallback = undefined;

	var _queue = new PriorityQueue((a, b) => a.priority > b.priority);

	var _this = this;

	// Keep track of unused geometries.
	// A geometry is tracked as unused if it was used by at least one model and then got removed from all models.
	// This map keep track of which geometries are unused (it stores geom.id -> geometry instance).
	var _unusedGeomsMap = new Map();

	// mem limits for cache cleanup
	var MB = 1024 * 1024;
	var _maxMemory = 100 * MB; // geometry limit at which cleanup is activated
	var _minCleanup = 50 * MB; // minimum amount of freed memory for a single cleanup run

	// Needed for cache-cleanup to check which RenderModels are loaded
	var _viewers = [];

	// Keeps track of requests from the OutOfCoreTileManager
	var _pendingRequests = [];

	this.addViewer = function (viewer) {
		_viewers.push(viewer);
	};

	this.removeViewer = function (viewer) {
		const index = _viewers.indexOf(viewer);

		if (index !== -1) {
			_viewers.splice(index, 1);
		}

		if (_viewers.length === 0) {
			this.dtor();
		}
	};

	this.dtor = function () {
		_viewers = [];

		_worker.removeEventListener('message', handleMessage);
		_worker.terminate();
		_worker = null;

		this.cleanup(true);

		_geoms = null;
		_mats = null;

		this.initialized = false;

		if (_timeout) {
			clearTimeout(_timeout);
			_timeout = null;
		}
		stopProcessing();
	};

	this.isIdle = function () {
		//The logic here is that the cache can be idle only after it has loaded something.
		//It will not be idle initially, when nothing has yet been loaded or queued for loading.
		//This ensures that code that waits for geometry loading to be "done" does not activate too early.
		//Note that this is consistent with calls to onGeomLoadComplete() which trigger only after _something_
		//is loaded.
		return _workTotal === 0 && _queue.isEmpty() && _lifeTimeTotal > 0;
	}

	this.addWorkInProgress = function(count) {
		if (_workTotal === 0) {
			_timings.push(performance.now());
		}

		_workTotal += count;
		for (let i = 0; i < _viewers.length; i++) {
			_viewers[i].setLoadingFpsTarget(true);
		}
	}

	this.updateProgress = function(count) {
		_workDone += count;

		//Signal loading progress to the attached viewers
		const loadProgressPercent = _workDone / _workTotal * 100;
		const loadProgressRounded = Math.round(loadProgressPercent);
		if (_currentProgress !== loadProgressRounded) {
			_currentProgress = loadProgressRounded;
			for (let i = 0; i < _viewers.length; i++) {
				_viewers[i].impl.signalProgress(_currentProgress, av.ProgressState.LOADING, null);
			}
		}

		//Reset progress percentage when we reach an idle state
		if (loadProgressPercent >= 100 && _workTotal > 0 && _workDone === _workTotal) {
			const t1 = performance.now();
			console.log(`Loading took ${t1 - _timings[_timings.length - 1]} (${t1 - _timings[0]})`);

			// Reset stats
			_lifeTimeTotal += _workTotal;
			_workTotal = 0;
			_workDone = 0;
			_currentProgress = -1;
			_requestsPerWorker = INITIAL_REQUESTS_PER_WORKER;

			stopProcessing();

			// Tell the viewer to repaint once we reach idle state (presumably we are done loading something)
			for (let i = 0; i < _viewers.length; i++) {
				_viewers[i].impl.onGeomLoadComplete();
				_viewers[i].setLoadingFpsTarget(false);
			}
		}
	}

	// function to handle messages from OtgLoadWorker (posted in onGeometryLoaded)
	function handleMessage(msg) {
		if (!msg.data) {
			return;
		}

		let i = 0;

		// Processing messages from the worker can be expensive, so we run it through the OOCTM task system.
		// This method will interrupt itself when running into the provided deadline, and continue work where it left
		// off in subsequent ticks.
		const processMessage = (deadline = Infinity) => {
			// It can happen that this task is kept alive in the OutOfCoreTileManager task queue after
			// the viewer has been destroyed. Once a new model is being opened, the task processing will
			// resume and an old task might get processed. The destructor for the geom cache will set _geoms
			// to null and we check here whether this is a task from a destroyed geomcache. In that case,
			// we just report true to indicate that the task can be cleared.
			if (_geoms === null) {
				return true;
			}

			// If the message indicates an error, we handle it and return true, telling the task system that we're done.
			if (msg.data.error) {
				var error = msg.data.error;
				// get hash for which request failed
				var hash = error.args ? error.args.hash : undefined;

				var type = error.args ? error.args.type : "g";

				// inform affected clients.
				if (hash) {
					if (type === "m") {
						_mats.set(hash, error); //create an error entry in the cache
						_this.fireEvent({type: MATERIAL_FAILED_EVENT, error: error});
						console.warn("Error loading material", hash);
					} else {
						_geoms.set(hash, error); //create an error entry in the cache
						_this.fireEvent({type: MESH_FAILED_EVENT, error: error});
						console.warn("Error loading mesh", hash);
					}

					const task = _hash2Requests.get(error.hash);
					task.callbacks.forEach(cb => cb(false));

					_hash2Requests.delete(error.hash);

					// track number of requests in progress
					_requestsInProgress--;
					_totalReceived++;
				}
				return true;
			} else if (msg.data.material) {
				// Just for the prism case, we might have to go async. Note that the out of core tile manager
				// doesn't know about this and the time budgeting will be less accurate.
				(async () => {
					var mdata = msg.data;
					var hash = mdata.hash;
					var matBlob = mdata.material;

					var mat = blobToJson(matBlob);

					if (avp.MaterialConverter.isPrismMaterial(mat)) {
						await avp.MaterialConverter.loadMaterialConverterPrismLibrary();
					}

					// add material to cache
					_mats.set(hash, mat);

					// pass material to all receiver callbacks
					_this.fireEvent({type: MATERIAL_RECEIVE_EVENT, material: mat, hash: hash});

					const task = _hash2Requests.get(mdata.hash);
					task.callbacks.forEach(cb => cb(true));

					_hash2Requests.delete(mdata.hash);

					_requestsInProgress--;
					_totalReceived++;
				})();
			} else {
				var meshlist = msg.data;

				for (i; i < meshlist.length; i++) {
					_requestsInProgress--;
					_totalReceived++;

					var mdata = meshlist[i];

					if (mdata.hash && mdata.mesh) {
						// convert geometry data to GeometryBuffer
						var geom = avp.meshToGeometry(mdata);

						// add geom to cache
						var hash = mdata.hash;
						_this.addGeometry(hash, geom);

						// free old unused geoms if necessary
						_this.cleanup();

						// pass geometry to all receiver callbacks
						_this.fireEvent({type: MESH_RECEIVE_EVENT, geom: geom});

						// Recall and store the load order importance, so we can later use it for unloading
						// In LMV, geom.importance and task.priority are two different things.
						// The former is set by the loader after loading a geometry, and based on the fragment's
						// bounding box. It is only used to prioritize cache cleanup.
						// The latter is a view-dependent load order prioritization.
						// This is similar in Tandem in general, but we still set importance to priority here,
						// because prioritizing cache cleanup isn't really that important.
						const task = _hash2Requests.get(mdata.hash);
						geom.importance = task.priority;

						task.callbacks.forEach(cb => cb(true));

						_hash2Requests.delete(mdata.hash);
					}

					// Interrupt the message handling when crossing the deadline.
					// Returning false tells the task system to invoke this handler again in one of the next ticks.
					// We then pick off where we left.
					if (i % 20 === 0 && performance.now() >= deadline) {
						i++;
						return false;
					}
				}
			}

			// All done.
			return true;
		};

		// We compute the priorities in a separate task since unpacking the messages from the work already
		// takes considerable time and has to be performed to determine the priorities.
		// This function will then create individual tasks.
		// Note that we could handle errors and materials directly, instead of deferring to another task,
		// but while parsing a single material is cheap, it may lead to the activation of many fragments.
		// It's also cleaner to handle all cases in a single place (processMessage).
		const createMessageProcessingTasks = () => {
			// Determine priority for this task as the maximum of the priority of all elements contained in the message.
			let priority = -Infinity;
			if (msg.data.material) {
				const mdata = msg.data;
				// material requests don't have a priority. We set the importances array expected by the worker
				// directly (see requestMaterial). Each request also only contains a single material.
				priority = _hash2Requests.get(mdata.hash).importances[0];
			} else if (!msg.data.error) {
				const meshlist = msg.data;
				for (let i = 0; i < meshlist.length; i++) {
					if (meshlist[i].hash && meshlist[i].mesh) {
						priority = Math.max(_hash2Requests.get(meshlist[i].hash)?.priority ?? -Infinity, priority);
					}
				}
			}

			// If no priority was specified, assume max priority
			if (priority === -Infinity) {
				priority = Infinity;
			}

			avp.OutOfCoreTileManager.addOneShotTask(processMessage, priority);

			return true;
		};

		avp.OutOfCoreTileManager.addOneShotTask(createMessageProcessingTasks, Infinity);
	}

	this.addGeometry = function(hash, geom) {
		_geoms.set(hash, geom);

		// track summed cache size in bytes
		_this.byteSize += geom.byteSize;

		if (Object.prototype.hasOwnProperty.call(geom, '_modelRefCount')) {
			return;
		}
		geom._modelRefCount = 0;

		const prototype = Object.getPrototypeOf(geom);
		if (Object.prototype.hasOwnProperty.call(prototype, 'modelRefCount')) {
			return;
		}
		Object.defineProperty(prototype, 'modelRefCount', {
			get: geometry_modelRefCount_get
		});
		prototype.setModelRefCount = geometry_setModelRefCount;
	};

	this.setGeomRefCount = function(geom, value) {
		if (geom._modelRefCount === 0 && value >= 1) {
			const unused = _unusedGeomsMap.get(geom.id);
			if (unused) {
				_unusedGeomsMap.delete(geom.id);
			}
		} else if (value === 0) {
			_unusedGeomsMap.set(geom.id, geom);
		}
		geom._modelRefCount = value;
	};

	this.initWorker = function (modelId, loadContext) {
		// Prepare load workers
		var msg = {
			operation: "INIT_WORKER_OTG",
			modelId
		};

		_worker.doOperation(initLoadContextGeomCache(msg, loadContext));
	};

	this.unregisterLoader = function(modelId, loadContext) {
		// Tell the worker that a loader has gone idle
		var msg = {
			operation: "UNREGISTER_LOADER_OTG",
			modelId
		};

		_worker.doOperation(initLoadContextGeomCache(msg, loadContext));
	}

	this.doPostLoad = function (loadContext) {
		//Tell the worker to wrap up loading and close sockets.
		var msg = {
			operation: "DO_POST_LOAD",
			endSession: _queue.isEmpty()
		};

		_worker.doOperation(initLoadContextGeomCache(msg, loadContext));
	};

	this.getGeometry = function(geomHash) {
		return _geoms.get(geomHash);
	};

	/**  Get a geometry from cache or load it.
	 *    @param {string}   url         - base request url of the geometry/ies resource -- geometry hash is added to the end of that when HTTP is used to load data
	 *    @param {string}   geomHash    - hash key to identify requested geometry/ies
	 *    @param {int} geomIdx          - the geometry ID/index in the model's geometry hash list (optional, pass 0 to skip use of geometry packs)
	 *    @param {string}   queryParams - additional param passed to file query
	 *    @param {Number} importance    - importance metric for the request geometry (e.g. surface area of the object bbox)
	 */
	this.requestGeometry = function (url, geomHash, geomIdx, queryParams, importance, loadContext) {

		// if this geometry is in memory, just return it directly
		var geom = _geoms.get(geomHash);
		if (geom && geom.args) {
			//it failed to load previously
			if (isNodeJS()) {
				setTimeout(() => this.fireEvent({
					type: MESH_FAILED_EVENT,
					error: geom,
					hash: geomHash,
					repeated: true
				}), 0);
			} else {
				this.fireEvent({type: MESH_FAILED_EVENT, error: geom, hash: geomHash, repeated: true});
			}
			return;
		} else if (geom) {
			//it was already cached
			if (isNodeJS()) {
				setTimeout(() => this.fireEvent({type: MESH_RECEIVE_EVENT, geom: geom}), 0);
			} else {
				this.fireEvent({type: MESH_RECEIVE_EVENT, geom: geom});
			}
			return;
		}

		// if geometry is already loading, just increment
		// the request counter.
		var task = _hash2Requests.get(geomHash);
		if (task && task.refcount) {
			task.refcount++;
			return;
		}

		// geom is neither in memory nor loading.
		// we have to request it.
		var msg = {
			loadContext: loadContext,
			operation: "LOAD_CDN_RESOURCE_OTG",
			type: "g",
			url: url,
			hash: geomHash,
			queryParams: queryParams,
			priority: importance,
			geomIdx: geomIdx,
			refcount: 1,
			requested: false,
			callbacks: []
		};

		_totalRequested++;

		_queue.push(msg);
		_hash2Requests.set(geomHash, msg);

		// Update max number of in-flight requests, based on queue length
		this._updateMaxRequests();

		startProcessing();
	};

	this.requestMaterial = function (url, matHash, matIdx, queryParams, loadContext) {

		// if this material is in memory, just return it directly
		var mat = _mats.get(matHash);
		if (mat && mat.args) {
			//it failed to load previously
			setTimeout(() => this.fireEvent({
				type: MATERIAL_FAILED_EVENT,
				error: mat,
				hash: matHash,
				repeated: true
			}), 0);
			return;
		} else if (mat) {
			//it was already cached
			setTimeout(() => this.fireEvent({type: MATERIAL_RECEIVE_EVENT, material: mat, hash: matHash}), 0);
			return;
		}

		// if material is already loading, just increment
		// the request counter.
		var task = _hash2Requests.get(matHash);
		if (task && task.refcount) {
			task.refcount++;
			return;
		}

		// material is neither in memory nor loading.
		// we have to request it.
		var msg = {
			operation: "LOAD_CDN_RESOURCE_OTG",
			type: "m",
			urls: [url],
			hashes: [matHash],
			queryParams: queryParams,
			refcount: 1,
			importances: [0x7fffffff], //use highest importance for materials
			requested: true,
			callbacks: []
		};

		_hash2Requests.set(matHash, msg);

		//Material requests are sent to the worker immediately, without going through the
		//priority queue.
		_worker.doOperation(initLoadContextGeomCache(msg, loadContext));
		_requestsInProgress++;
		_totalRequested++;
	};

	function startProcessing() {
		if (!_recurringProcessQueuedItemsCallback) {
			_recurringProcessQueuedItemsCallback = () => { return processQueuedItems(); };
			avp.OutOfCoreTileManager.addRecurringTask(_recurringProcessQueuedItemsCallback);
		}
	}

	function stopProcessing() {
		if (_recurringProcessQueuedItemsCallback) {
			avp.OutOfCoreTileManager.removeRecurringTask(_recurringProcessQueuedItemsCallback);
			_recurringProcessQueuedItemsCallback = null;
		}
	}

	/**
	 * Requests a batch of geometries and/or materials by their hashes.
	 * All resources must have been requested via requestGeometry / requestMaterial before, but must not have been
	 * loaded yet (i.e. must still be pending). It is the caller's responsibility to only provide hashes that meet
	 * these conditions.
	 * @param {Set<string>} hashes Set of resource hashes to request.
	 * @param {number} priority Priority with which to process the response.
	 * @returns {Promise<boolean>} Promise resolving to true if all loaded successfully, false otherwise.
	 */
	this.batchRequest = function(hashes, priority) {
		let numMissingHashes = hashes.size;
		let loadCompleted;
		let promise = new Promise((resolve) => {
			loadCompleted = resolve;
		});
		let allLoadedSuccessfully = true;

		const callback = (success) => {
			--numMissingHashes;
			if (!success) {
				// A required geom or material failed to load.
				allLoadedSuccessfully = false;
			}
			if (numMissingHashes === 0) {
				loadCompleted(allLoadedSuccessfully);
			}
		};

		for (const hash of hashes) {
			// All hashes have previously been requested via loadGeometry / -material, so there are pending requests
			// for them.
			let request = _hash2Requests.get(hash);

			// requestMaterial uses setTimeout to fire the received event if the material is already there.
			// So it can happen that materials get requested by the loader that are actually already known to the OtgResourceCache.
			// So we need to catch that here.
			if (_mats.has(hash)) {
				// Because of the setTimeout, we must delay the callback.
				// Otherwise, if we fire the callback here immediately, consolidation will be attempted before the
				// loader received the material.
				setTimeout(() => callback(true), 0);
				continue;
			}

			request.callbacks.push(callback);

			if (request.requested) {
				continue;
			}

			// Only geometry requests and handled beyond this point. Materials are always immediately requested,
			// so the don't pass the check above.

			request.requested = true;

			request.priority = Math.max(priority, request.priority ?? 0);

			_pendingRequests.push(request);
		}

		// If we accumulated enough work (completely arbitrary number),
		// or no more work will come this tick (because the max number of in flight requests has been reached),
		// send it immediately to keep the worker busy.
		if (_pendingRequests.length > 1000 || _requestsInProgress + _pendingRequests.length >= _requestsPerWorker) {
			sendGeometryRequests(_pendingRequests);
			_pendingRequests = [];
		} else if (!_timeout && _pendingRequests.length > 0) {
			// Otherwise, delay sending the requests to batch with other nodes.
			// In particular when loading small nodes from cache, we don't want to send 100s of messages per tick.
			// This will be outside of the OOCTM time budget, but should be fast for < 1000 requests.
			_timeout = setTimeout(() => {
				_timeout = null;
				sendGeometryRequests(_pendingRequests);
				_pendingRequests = [];
			}, 0);
		}

		return promise;
	};


	function processQueuedItems() {
		// Geometry loading works by pushing requests into the queue as fragments get loaded.
		// So we start out by loading from the queue, and then cut over to the OOCTM when the BVH got ready.
		// The OOCTM prioritizes load requests per node and calls batchRequest for the hashes that haven't been loaded
		// yet. At this point, we want to skip the queue as long as the OOCTM still has download tasks.
		// Ideally we'd clear the queue entirely as soon as the OOCTM sends work, but if a very small
		// model finished, a large model might still not have a BVH and want to process the queue.
		if (avp.OutOfCoreTileManager.hadDownloadTasksLastTick()) {
			return;
		}

		var howManyCanWeDo = getBandwidthBudget() - _requestsInProgress;

		// TODO: May want to avoid very small requests (see LMV).

		if (howManyCanWeDo <= 0) {
			return;
		}

		var requests = [];

		while (!_queue.isEmpty() && requests.length < howManyCanWeDo) {

			var task = _queue.pop();

			if (task.requested) {
				continue;
			}

			// Can happen when cutting over to loading from BVH. Then we still need to process the queue to clear it.
			if (_geoms.has(task.hash)) {
				continue;
			}

			requests.push(task);
			task.requested = true;
		}

		sendGeometryRequests(requests);
	}

	// This is called sendRequests in LMV, but it only handles geometries.
	function sendGeometryRequests(requests) {
		if (requests.length === 0) {
			return;
		}

		const msg = {
			operation: "LOAD_CDN_RESOURCE_OTG",
			type: "g",
			urls: requests.map(r => r.url),
			hashes: requests.map(r => r.hash),
			// Legacy mismatch: The worker expects an array of importances,
			// while they are now called priority on individual tasks.
			importances: requests.map(r => r.priority),
			queryParams: requests[0].queryParams,
		};

		// send request to worker
		_worker.doOperation(initLoadContextGeomCache(msg, requests[0].loadContext));
		_requestsInProgress += requests.length;
	};

	// remove all open requests of this client
	// input is a map whose keys are geometry hashes
	this.cancelRequests = function (geomHashMap) {

		for (var [hash] of geomHashMap) {
			var task = _hash2Requests.get(hash);

			if (task)
				task.refcount--;
			/*
			if (task.refcount === 1) {
				_hash2Requests.delete(hash);
			}*/
		}

		var hiPrioList = [];
		var heap = _queue._heap;
		for (var i = 0; i < heap.length; i++) {
			var t = heap[i];
			var req = _hash2Requests.get(t.hash);
			if (req) {
				if (req.refcount)
					hiPrioList.push(t);
				else
					_hash2Requests.delete(t.hash);
			}
		}

		//TODO: perhaps we can leave requests with refcount = 0 in the queue
		//but sort the queue based on refcount so that those get deprioritized

		_totalRequested -= heap.length - hiPrioList.length;

		//Reset the priority queue with the remaining items
		_queue._heap.length = 0;
		for (let i=0; i<hiPrioList.length; i++) {
			_queue.push(hiPrioList[i]);
		}

		// TODO: To make switches faster, we should also inform the worker thread,
		//       so that it doesn't spend too much time with loading geometries that noone is waiting for.

		if (hiPrioList.length === 0) {
			//Make sure geom load event gets fired if the queue is all of a sudden empty
			for (let i = 0; i < _viewers.length; i++) {
				_viewers[i].impl.onGeomLoadComplete();
			}
		}
	};

	this.cleanup = function (force) {

		if (_unusedGeomsMap.size === 0 || (!force && this.byteSize < _maxMemory)) {
			return;
		}

		var _unusedGeoms = [];
		for (let geom of _unusedGeomsMap.values()) {
			_unusedGeoms.push(geom);
		}

		// Sort unused geoms by ascending importance
		_unusedGeoms.sort(compareGeomsByImportance);

		// Since cleanup is too expensive to run per geometry,
		// we always remove a bit more than strictly necessary,
		// so that we can load some more new geometries before we have to
		// run cleanup again.
		var targetMem = force ? 0 : _maxMemory - _minCleanup;

		// Remove geoms until we reach mem target
		var i = 0;
		for (; i < _unusedGeoms.length && this.byteSize >= targetMem; i++) {

			var geom = _unusedGeoms[i];

			// remove it from cache
			_geoms.delete(geom.hash);
			_unusedGeomsMap.delete(geom.id);

			// update mem consumption. Note that we run this only for geoms that
			// are not referenced by any RenderModel in memory, so that removing them
			// should actually free memory.
			this.byteSize -= geom.byteSize;

			// Dispose GPU mem.
			// NOTE: In case we get performance issues in Chrome, try commenting this out
			// (see hack in GeometryList.dispose)
			geom.dispose();
		}
	};

	this.getGeometry = function (hash) {
		return _geoms.get(hash);
	};

	/**
	 * Informs the ResourceCache that a fragment list download has started and that
	 * the download of geometries should be throttled while the download is ongoing.
	 */
	this.throttleDownload = function() {
		_numLoadersDownloadingFragmentList++;
	}

	/**
	 * Stops throttling the download of geometries after the fragment list download has completed.
	 */
	this.stopThrottleDownload = function() {
		_numLoadersDownloadingFragmentList--;
	}

	this._updateMaxRequests = function() {
		// A very simple heuristic to increase the amount of in-flight requests depending on the queue length.
		// We start with a small value (see INITIAL_REQUESTS_PER_WORKER) to enable meaningful prioritization and then
		// increase it depending on the queue length, until the maximum value is reached.
		// Note that this method does never decrease the current value. Resetting to the default is handled separately
		// (currently once the queue is empty).
		_requestsPerWorker = Math.min(Math.max(Math.round(_queue.size() * 0.1), _requestsPerWorker), MAX_REQUESTS_PER_WORKER);
	}

	this.hasBandwidthBudget = function() {
		return _requestsInProgress < getBandwidthBudget();
	}

	function getBandwidthBudget() {
		if (_numLoadersDownloadingFragmentList > 0) {
			return INITIAL_REQUESTS_PER_WORKER;
		}
		return _requestsPerWorker;
	}
}

av.EventDispatcher.prototype.apply(DtResourceCache.prototype);

// These two functions are intentionally defined outside of the
// upper scope to make sure they don't hold a reference to the
// closure. They will be added to a prototype later and thus
// would prevent the closure from being garbage collected.
function geometry_modelRefCount_get() {
  return this._modelRefCount;
}

let warningAboutRefcountPrinted = false;
function geometry_setModelRefCount(value, geomCache) {
    if (!geomCache) {
        // This should never happen, but as a fallback we will set the refcounts to 1
        // if we don't have a geomCache to keep track of them.
        if (!warningAboutRefcountPrinted) {
            console.warn("geometry_setModelRefCount: geomCache is not defined");
            warningAboutRefcountPrinted = true;
        }
        this._modelRefCount = 1;
    } else {
        geomCache.setGeomRefCount(this, value);
    }
}
